import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
// components
import { Divider, Icon, IconButton, InputAdornment } from "@material-ui/core";
import { Grid, Button, Card, List, ListItem, ListItemText, TextField } from "@material-ui/core";
import BouncingDots from "../loading/BouncingDots";
import { useDropzone } from "react-dropzone";
import { getCompaniesForAdmin } from "../../reducers/userCompanies";
import { requestCompanies } from "../../actions/userActions";
import type { Company } from "../../types/types";
import { IDestinationGroupingParents } from "../../types/stores/destinationGroupingParents";
import { ISubDestination } from "../../types/stores/vars";

import destinationStyles from "../../components/modals/admin/DestinationUIStyles";

const useStyles = destinationStyles;

type Props = {
    handleHaveChanges?: Function;
    onChangeParent: (data: IDestinationGroupingParents) => void;
    modalProps: {
        parentId: number;
        parentName: string;
        parentDesc: string;
        parentIcon: string;
        SubDestinations: Array<ISubDestination>;
    };
    parentId: number;
};

const DestinationGroupingParentsIconImport: React.FC<Props> = ({
    handleHaveChanges,
    onChangeParent,
    modalProps,
    parentId,
}: Props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [diff, setDiff] = useState("");
    const [diffIcon, setDiffIcon] = useState("");
    const [parentName, setParentName] = useState("");
    const [parentDesc, setParentDesc] = useState("");
    const [parentIcon, setParentIcon] = useState("");
    const [uploadData, setUploadData] = useState("");
    const [isReadingBase64, setIsReadingBase64] = useState(false);
    const [subDestinations, setSubDestinations] = useState<Array<ISubDestination>>([]);
    const [localDestinationGroupingParent, setLocalDestinationGroupingParent] = useState<IDestinationGroupingParents>();

    const companies = useSelector(state => getCompaniesForAdmin(state));
    const [leftCompanies, setLeftCompanies] = useState<Array<Company>>([]);
    const [rightCompanies, setRightCompanies] = useState<Array<Company>>([]);
    const [checked, setChecked] = useState<any[]>([]);
    const [leftSearch, setLeftSearch] = useState("");
    const [rightSearch, setRightSearch] = useState("");

    function intersectionInCompanies(a, b) {
        const c = b.filter(el => a.includes(el.CompanyID));
        const d = c.map(x => x.CompanyID);
        return d;
    }

    const leftChecked = intersectionInCompanies(checked, leftCompanies);
    const rightChecked = intersectionInCompanies(checked, rightCompanies);

    // Request User and Company Data
    useEffect(() => {
        setParentName(modalProps.parentName);
        setParentDesc(modalProps.parentDesc);
        setParentIcon(modalProps.parentIcon);
        setSubDestinations(modalProps.SubDestinations);
        const initialState: IDestinationGroupingParents = {
            DestinationGroupingParentID: parentId,
            ParentName: modalProps.parentName,
            ParentIcon: modalProps.parentIcon,
            Description: modalProps.parentDesc,
            SubDestinations: modalProps.SubDestinations || [],
        };
        setLocalDestinationGroupingParent(initialState);
    }, [modalProps, parentId]);

    useEffect(() => {
        dispatch(requestCompanies());
    }, [dispatch]);

    //handle HaveChanges
    useEffect(() => {
        if (handleHaveChanges) {
            handleHaveChanges(parentName?.length > 0 && (diff?.length > 0 || diffIcon.length > 0));
        }
    }, [diff, diffIcon, parentName, handleHaveChanges]);

    useEffect(() => {
        const newDGParent = localDestinationGroupingParent;
        if (newDGParent != null && diffIcon.length > 0) {
            newDGParent!.ParentIcon = uploadData;
            onChangeParent(newDGParent!);
        }
    }, [diffIcon, localDestinationGroupingParent, onChangeParent, uploadData]);

    const rightCompaniesRef = useRef(rightCompanies);
    const subDestinationsRef = useRef(subDestinations);

    useEffect(() => {
        rightCompaniesRef.current = rightCompanies;
        subDestinationsRef.current = subDestinations;
    }, [rightCompanies, subDestinations]);

    useEffect(() => {
        let sortedCompanies = companies.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        sortedCompanies = sortedCompanies.filter(el => !rightCompaniesRef.current.includes(el));
        setLeftCompanies(sortedCompanies);

        if (companies && companies.length > 0 && subDestinationsRef.current != null) {
            const tmpChecked = subDestinationsRef.current.map(sub => sub.CompanyId);
            setChecked(tmpChecked);

            const c = companies.filter(el => tmpChecked.includes(el.CompanyID));
            const d = rightCompaniesRef.current.concat(c);
            setRightCompanies(d);

            const e = intersectionInCompanies(tmpChecked, sortedCompanies); // leftChecked not stored
            const f = notInCompanies(sortedCompanies, e); // leftCompanies
            setLeftCompanies(f); // setLeftCompanies(notInCompanies(leftCompanies, leftChecked));

            setChecked([]);
        }
    }, [dispatch, companies]);

    const onDrop = useCallback(acceptedFiles => {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = event => {
            setIsReadingBase64(true);

            if (event && event.target && event.target.result) {
                const tmpStr = event.target.result.toString() || "";
                setUploadData(tmpStr);
                setIsReadingBase64(false);
                setDiffIcon(file.name);
            }
        };
    }, []);
    const resetUploadData = () => {
        setUploadData("");
        if (diffIcon.length > 0) {
            setDiffIcon("");
            const newDGParent = localDestinationGroupingParent;
            newDGParent!.ParentIcon = modalProps.parentIcon;
            onChangeParent(newDGParent!);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ".png, .gif, .svg, .jpg, .jpeg",
    });

    function not(a, b) {
        return a.filter(value => b.indexOf(value) === -1);
    }

    function notInCompanies(a, b) {
        return a.filter(value => b.indexOf(value.CompanyID) === -1);
    }

    const handleToggle = value => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleCheckedRight = () => {
        const c = companies.filter(el => leftChecked.includes(el.CompanyID));
        const d = rightCompanies.concat(c);
        let sortedD = d.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        setRightCompanies(sortedD);
        setLeftCompanies(notInCompanies(leftCompanies, leftChecked));
        setChecked(not(checked, leftChecked));
        const newDGParent = localDestinationGroupingParent;
        let subDestinationDefault = new Array<ISubDestination>();
        d.forEach(x => {
            subDestinationDefault.push({
                CompanyId: x.CompanyID,
                DestinationGroupingParentID: parentId,
            } as ISubDestination);
        });
        newDGParent!.SubDestinations = subDestinationDefault;
        onChangeParent(newDGParent!);
        setDiff("SubDestinations");
    };

    const handleCheckedLeft = () => {
        const c = companies.filter(el => rightChecked.includes(el.CompanyID));
        const e = leftCompanies.concat(c);
        let sortedE = e.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        setLeftCompanies(sortedE);
        const d = notInCompanies(rightCompanies, rightChecked);
        //setRightCompanies(notInCompanies(rightCompanies, rightChecked));
        setRightCompanies(d);
        setChecked(not(checked, rightChecked));
        const newDGParent = localDestinationGroupingParent;
        let subDestinationDefault = new Array<ISubDestination>();
        d.forEach(x => {
            subDestinationDefault.push({
                CompanyId: x.CompanyID,
                DestinationGroupingParentID: parentId,
            } as ISubDestination);
        });
        newDGParent!.SubDestinations = subDestinationDefault;
        onChangeParent(newDGParent!);
        setDiff("SubDestinations");
    };

    const handleSetParentName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setParentName(e.target.value);
        const newDGParent = localDestinationGroupingParent;
        newDGParent!.ParentName = e.target.value;
        onChangeParent(newDGParent!);
        setDiff("ParentName");
    };

    const handleSetParentDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
        setParentDesc(e.target.value);
        const newDGParent = localDestinationGroupingParent;
        newDGParent!.Description = e.target.value;
        onChangeParent(newDGParent!);
        setDiff("Description");
    };

    const handleLeftSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLeftSearch(e.target.value);
    };

    const handleRightSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRightSearch(e.target.value);
    };

    const filteredLeftCompanies = leftCompanies.filter(company =>
        company.CompanyName.toLowerCase().includes(leftSearch.toLowerCase())
    );

    const filteredRightCompanies = rightCompanies.filter(company =>
        company.CompanyName.toLowerCase().includes(rightSearch.toLowerCase())
    );

    const customList = items => (
        <Card>
            <Divider />
            <List className={classes.list} component="div" role="list">
                {items.map(value => {
                    const labelId = `transfer-list-all-item-${value.CompanyID}-label`;
                    return (
                        <ListItem
                            key={value.CompanyID}
                            role="listitem"
                            button
                            onClick={handleToggle(value.CompanyID)}
                            className={
                                checked.indexOf(value.CompanyID) !== -1 ? classes.cardShadow : classes.hoverShadow
                            }
                        >
                            <div className={`${classes.sideContainer} ${classes.circleRight}`}>
                                {checked.indexOf(value.CompanyID) !== -1 && (
                                    <Icon fontSize="large" className={classes.checkCircle}>
                                        check_circle
                                    </Icon>
                                )}
                            </div>
                            <IconButton color="primary" aria-label="item" style={{ marginLeft: "10px" }}>
                                <i className="material-icons">drag_indicator</i>
                            </IconButton>
                            <ListItemText id={labelId} primary={`${value.CompanyName}`} />
                        </ListItem>
                    );
                })}
                <ListItem />
            </List>
        </Card>
    );

    return (
        <div>
            <div
                id="browse-files-modal"
                style={{ overflow: "hidden", display: "flex", justifyContent: "center", width: "100%" }}
            >
                <div className="col col-xs-6 col-sm-6">
                    <div style={{ marginLeft: "0px", paddingBottom: "20px" }}>
                        <TextField
                            label="Name *"
                            style={{
                                // marginLeft: "15px",
                                width: "95%",
                            }}
                            id="ParentName"
                            type={"text"}
                            onChange={handleSetParentName}
                            value={parentName}
                        />
                    </div>
                    <div style={{ marginLeft: "0px", paddingBottom: "20px" }}>
                        <TextField
                            label="Description"
                            style={{
                                // marginLeft: "15px",
                                width: "95%",
                            }}
                            id="Description"
                            type={"text"}
                            onChange={handleSetParentDescription}
                            value={parentDesc}
                        />
                    </div>
                </div>
                <div className="col col-xs-6 col-sm-6">
                    <label id="gridLabel">Logo</label> - .png, .gif, .svg, .jpg, .jpeg
                    <div className="my-computer-files-container" style={{ width: "100%" }}>
                        <div {...getRootProps()} className="browse-drag-and-drop-container" style={{ height: "120px" }}>
                            <input {...getInputProps()} className="browse-drag-and-drop-container-label" />
                            {isDragActive ? (
                                <p>Drop the files here ...</p>
                            ) : (
                                <div style={{ textAlign: "center", placeContent: "center" }}>
                                    {parentId > -1 && parentIcon != null && parentIcon.length > 0 && (
                                        <>
                                            <img
                                                id="parentIcon"
                                                src={parentIcon}
                                                width="50px"
                                                height="50px"
                                                style={{ justifyContent: "center" }}
                                            />
                                            <br />
                                            <br />
                                        </>
                                    )}
                                    Drag and drop from your computer or <ins>browse</ins> to upload ...
                                </div>
                            )}
                        </div>
                        <div className="files-listing">
                            {/* {selectedImages.length > 0 &&                            
                                selectedImages.map((image, index) => (
                                    <img src={`${URL.createObjectURL(image)}`} key={index} alt="" />
                                ))} */}
                            {isReadingBase64 ? (
                                <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                                    <BouncingDots />
                                </div>
                            ) : (
                                uploadData.length > 0 && (
                                    <div className="col-md-6">
                                        <label id="tempIconLabel" style={{ marginRight: "10px" }}>
                                            Selected Logo
                                        </label>
                                        <img id="tempIcon" src={uploadData} width="50px" height="50px" />
                                        <IconButton
                                            color="secondary"
                                            onClick={() => resetUploadData()}
                                            aria-label="delete uploaded logo"
                                            style={{ marginLeft: "10px" }}
                                        >
                                            <i className="material-icons">delete</i>
                                        </IconButton>
                                    </div>
                                )
                            )}
                            {/* <aside>{thumbs}</aside> */}
                        </div>
                    </div>
                </div>
            </div>
            <div
                id="browse-files-modal"
                style={{ overflow: "hidden", display: "flex", justifyContent: "center", width: "100%" }}
            >
                <div className="col col-xs-12 col-sm-12">
                    <Divider style={{ width: "100%" }} />
                    <label id="gridLabel" style={{ paddingTop: "20px", paddingBottom: "20px" }}>
                        Define Destination Group Contents
                    </label>
                    {companies.length <= 0 ? (
                        <div className={classes.root}>
                            <BouncingDots />
                        </div>
                    ) : (
                        <Grid
                            container
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                            className={classes.root}
                        >
                            <Grid item>
                                <TextField
                                    placeholder="Search"
                                    style={{ marginBottom: "10px" }}
                                    type="text"
                                    onChange={handleLeftSearchChange}
                                    value={leftSearch}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <i className="material-icons filter-icon filter-icon-black">search</i>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {customList(filteredLeftCompanies)}
                            </Grid>
                            <Grid item>
                                <Grid container direction="column" alignItems="center">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        className={classes.button}
                                        onClick={handleCheckedRight}
                                        disabled={leftChecked.length === 0}
                                        aria-label="move selected right"
                                        style={{ fontSize: "16px", color: "0000DE", marginRight: "10px" }}
                                    >
                                        &gt;
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        className={classes.button}
                                        onClick={handleCheckedLeft}
                                        disabled={rightChecked.length === 0}
                                        aria-label="move selected left"
                                        style={{ fontSize: "16px", color: "0000DE", marginRight: "10px" }}
                                    >
                                        &lt;
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <TextField
                                    placeholder="Search"
                                    style={{ marginBottom: "10px" }}
                                    type="text"
                                    onChange={handleRightSearchChange}
                                    value={rightSearch}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <i className="material-icons filter-icon filter-icon-black">search</i>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {customList(filteredRightCompanies)}
                            </Grid>
                        </Grid>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DestinationGroupingParentsIconImport;
